// The warranty journey's activity log, made readable: hide the internal plumbing unless asked, and
// fold a run of the same event into one row ("Invoice viewed by rider ×15"). Presentation only:
// the label, group and fold key come from the service (all-warranties-contract.md, 13 Oct 2026).
import { formatDate, formatDateTime } from "../common/format";

export const ACTIVITY_GROUPS = ["milestone", "detail", "internal"];

// An unknown or missing group reads as detail, so an event from an older service is still shown.
export const groupOf = (event) => (ACTIVITY_GROUPS.includes(event?.group) ? event.group : "detail");

export const visibleEvents = (events, showAll) =>
  (Array.isArray(events) ? events : []).filter((event) => showAll || groupOf(event) !== "internal");

// Two events fold when they are next to each other with the same fold key, actor and label. An
// event without a fold key never folds.
const foldIdentity = (event) =>
  typeof event?.foldKey === "string" && event.foldKey !== ""
    ? [event.foldKey, event.actor?.type || "", event.label || ""].join("\u0000")
    : null;

// The rows to draw, newest first like the events. Each row keeps its events (newest first), so it
// can open into them. A row's key is its newest event's place in the loaded list, which does not
// change when an older page is appended; only the last row can grow then.
export const foldEvents = (events) => {
  const rows = [];
  (Array.isArray(events) ? events : []).forEach((event, index) => {
    const identity = foldIdentity(event);
    const previous = rows[rows.length - 1];
    if (identity !== null && previous && previous.identity === identity) {
      previous.events.push(event);
      return;
    }
    rows.push({ identity, index, events: [event] });
  });
  return rows.map(({ index, events: rowEvents }) => {
    const newest = rowEvents[0];
    return {
      key: `${index}-${newest.at || ""}-${newest.kind || ""}-${newest.entityId || ""}`,
      label: newest.label || newest.kind || "-",
      kind: newest.kind,
      actor: newest.actor,
      group: groupOf(newest),
      count: rowEvents.length,
      newestAt: newest.at,
      oldestAt: rowEvents[rowEvents.length - 1].at,
      events: rowEvents,
    };
  });
};

const clockOf = (date) => {
  const hours = date.getHours();
  return {
    text: `${hours % 12 === 0 ? 12 : hours % 12}:${String(date.getMinutes()).padStart(2, "0")}`,
    period: hours < 12 ? "am" : "pm",
  };
};

// "13 Oct 2026, 6:16–6:18 pm" for a run inside one day, both ends in full across days.
export const formatTimeRange = (oldestAt, newestAt) => {
  const from = new Date(oldestAt);
  const to = new Date(newestAt);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return formatDateTime(newestAt);
  }
  if (from.toDateString() !== to.toDateString()) {
    return `${formatDateTime(oldestAt)} – ${formatDateTime(newestAt)}`;
  }
  const a = clockOf(from);
  const b = clockOf(to);
  const day = formatDate(newestAt);
  if (a.text === b.text && a.period === b.period) {
    return `${day}, ${b.text} ${b.period}`;
  }
  if (a.period === b.period) {
    return `${day}, ${a.text}–${b.text} ${b.period}`;
  }
  return `${day}, ${a.text} ${a.period}–${b.text} ${b.period}`;
};

// The viewer's "Show all activity" choice, kept in this browser only. Storage can be missing or
// refuse (private window, blocked site data); the screen then starts with internal rows hidden.
export const SHOW_ALL_STORAGE_KEY = "omsAdmin.warrantyJourney.showAllActivity";

export const readShowAll = () => {
  try {
    return window.localStorage.getItem(SHOW_ALL_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

export const writeShowAll = (value) => {
  try {
    window.localStorage.setItem(SHOW_ALL_STORAGE_KEY, value ? "1" : "0");
  } catch {
    // why: a remembered toggle is a convenience; the screen works the same without it.
  }
};
